import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideLifecycleProps {
  onContinue: () => void;
}
export function SlideLifecycle({
  onContinue
}: SlideLifecycleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600); // axes
    const t2 = setTimeout(() => setStage(2), 1400); // main curve
    const t3 = setTimeout(() => setStage(3), 4000); // phase labels + bands
    const t4 = setTimeout(() => setStage(4), 5800); // potential area
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  const mainCurve = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
  const potentialCurve = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
  const potentialArea = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
  const phases = [{
    id: 'rd',
    label: 'R&D',
    sub: 'Investment & clinical trials',
    x: 230
  }, {
    id: 'launch',
    label: 'Launch',
    sub: 'Heavy marketing spend',
    x: 420
  }, {
    id: 'mature',
    label: 'Mature',
    sub: 'Peak revenue & share',
    x: 595
  }, {
    id: 'legacy',
    label: 'Legacy',
    sub: 'Loss of exclusivity',
    x: 845,
    accent: true
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-707" data-mp="388htqllxz7mr">
      {/* Snakker logo top-right */}
      <div className="absolute top-6 right-8 md:top-10 md:right-12 flex items-center gap-2 z-10" data-id="element-708" data-mp="3383o1a7tc9rw">
        <div className="w-8 h-8 rounded-lg bg-teal-400 flex items-center justify-center" data-id="element-709" data-mp="ngzbr39u3jqt">
          <div className="w-3.5 h-3.5 rounded-full bg-white" data-id="element-710" data-mp="iio14qbpt57j" />
        </div>
        <span className="text-teal-400 font-bold text-base tracking-tight" data-id="element-711" data-mp="pnfeh5t3p6ea">
          snakker
        </span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-712" data-mp="ukfjdde7s33t">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-713" data-mp="14jek89sqmug9">
          The hidden legacy potential
        </h2>
      </motion.div>

      <div className="flex-1 relative flex items-center justify-center" data-id="element-714" data-mp="bv8xjhi4ivds">
        <div className="w-full max-w-5xl relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-715" data-mp="bviop8ewn8l2">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-716" data-mp="lkskay8yj9dx">
            <defs data-id="element-717" data-mp="3kbuatahsgamp">
              <pattern id="hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-718" data-mp="3ka9h1n3asvpw">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-719" data-mp="26e84resfj66i" />
              </pattern>
            </defs>

            {/* + and − axis labels */}
            <motion.text x="60" y="100" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-720" data-mp="1ng58qg1gs8z7">
              +
            </motion.text>
            <motion.text x="60" y="385" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-721" data-mp="19ql9hk73dtun">
              −
            </motion.text>

            <motion.text x="30" y="240" textAnchor="middle" className="fill-white/40 text-xs font-medium" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-722" data-mp="34qlsui4mn6en">
              Cumulative Value
            </motion.text>
            <motion.text x="520" y="455" textAnchor="middle" className="fill-white/40 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-723" data-mp="275zisxun0x05">
              Time
            </motion.text>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-724" data-mp="1urufu018y2vv" />

            {/* Zero line */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-725" data-mp="29hrwzw0rdsbk" />
            <motion.text x="975" y="295" className="fill-white/30 text-[10px] font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            delay: 0.4
          }} data-id="element-726" data-mp="o7bc1zq0g4fg">
              €0
            </motion.text>

            {/* Phase dividers */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.5 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-727" data-mp="3773w8ub53vme">
              <line x1="380" y1="50" x2="380" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-728" data-mp="3kxzbqpg59ayy" />
              <line x1="460" y1="50" x2="460" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-729" data-mp="3ux82l9fpq9h3" />
              <line x1="730" y1="50" x2="730" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-730" data-mp="3ube1qrbkcvi" />
            </motion.g>

            {/* Highlight band on Legacy phase */}
            <motion.rect x="730" y="50" width="230" height="380" fill="#00D4AA" fillOpacity="0.04" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-731" data-mp="rn1m3ow3qc0i" />

            {/* Potential hatched area */}
            <motion.path d={potentialArea} fill="url(#hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-732" data-mp="15jf5x4o3ypi5" />
            <motion.path d={potentialArea} fill="rgba(0, 212, 170, 0.08)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-733" data-mp="1k9xdm0t4wf8s" />

            {/* Main curve */}
            <motion.path d={mainCurve} fill="none" stroke="#0f172a" strokeWidth="3" strokeOpacity="0.95" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-734" data-mp="1y3evlh669jwp" />

            {/* Potential dashed extension */}
            <motion.path d={potentialCurve} fill="none" stroke="#00D4AA" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-735" data-mp="30waqgx2s9jbu" />

            {/* Phase labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-736" data-mp="2r44wxiwvutuj">
              {phases.map(p => <g key={p.label} data-id="element-737" data-mp="1vdhruomnow0v">
                  <text x={p.x} y="450" textAnchor="middle" className={`text-sm font-semibold ${p.accent ? 'fill-teal-400' : 'fill-white/65'}`} data-id="element-738" data-mp="1vcotoc1rjeuc">
                    {p.label}
                  </text>
                  <text x={p.x} y="468" textAnchor="middle" className={`text-xs ${p.accent ? 'fill-teal-300/60' : 'fill-white/35'}`} data-id="element-739" data-mp="1hcxalwwe389h">
                    {p.sub}
                  </text>
                </g>)}
            </motion.g>

            {/* POTENTIAL label */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-740" data-mp="yo0m69nob9z6">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#00D4AA" strokeWidth="1" strokeOpacity="0.6" data-id="element-741" data-mp="30d69kdzuz7ov" />
              <text x="908" y="170" className="fill-teal-400 text-sm font-bold tracking-wider" data-id="element-742" data-mp="3abc7ylmghvoz">
                POTENTIAL
              </text>
              <text x="908" y="186" className="fill-teal-300/60 text-[10px]" data-id="element-743" data-mp="2hnzz1e97xme5">
                Softening the cliff
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-744" data-mp="2qmlciuzu2b82">
              <text x="230" y="410" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-745" data-mp="22whags2amjj6">
                Investment
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-746" data-mp="316z9k2zk9jyj">
              <text x="595" y="55" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-747" data-mp="2nbetdfb7vfut">
                Peak Revenue
              </text>
            </motion.g>
          </svg>

          {/* Floating insight card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 4 ? 1 : 0,
          y: stage >= 4 ? 0 : 20
        }} transition={{
          duration: 0.5,
          delay: 0.7
        }} className="absolute bottom-2 left-0 bg-white/95 border border-teal-400/30 shadow-lg p-4 rounded-xl max-w-sm backdrop-blur-sm" data-id="element-748" data-mp="2sarn8k0i9n6w">
            <h4 className="text-teal-400 font-semibold mb-1 text-sm" data-id="element-749" data-mp="552lv1tvube9">
              The Strategic Opportunity
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-750" data-mp="a4fek37fqcor">
              In the Legacy phase, the dashed teal line shows what's possible
              with continuous optimization — slowing the decline and extending
              the revenue tail by even a few percentage points.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="absolute -left-20 top-0 bottom-0 flex flex-col justify-between text-xs text-white/40 font-medium py-4" data-id="element-751" data-mp="t0f1v0pgws20">
        <span data-id="element-752" data-mp="1gpq4knqdowis">Revenue</span>
        <span data-id="element-753" data-mp="1lhrcxykfe33j">Investment</span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 5 ? 1 : 0,
      y: stage >= 5 ? 0 : 20
    }} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-754" data-mp="1lij8xahxq2uc">
        <div className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 space-y-3" data-id="element-755" data-mp="3k04jd0tpouy7">
          <h3 className="text-teal-400 font-bold text-lg" data-id="element-756" data-mp="3f1tbuwqw2wj5">The Focus</h3>
          <p className="text-white/70 leading-relaxed" data-id="element-757" data-mp="3f3xmdxjamtse">
            Agencies and internal teams focus 90% of their effort on Launch and
            Mature phases, where budgets are highest.
          </p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-6 space-y-3 relative overflow-hidden" data-id="element-758" data-mp="6u4cohp7zqws">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -mr-16 -mt-16" data-id="element-759" data-mp="1vt6qi21q0ju" />
          <h3 className="text-purple-400 font-bold text-lg" data-id="element-760" data-mp="zbgppl3qnc0y">The Opportunity</h3>
          <p className="text-white/90 leading-relaxed relative z-10" data-id="element-761" data-mp="ujgmnjsdg305">
            Legacy brands generate massive volume but receive near-zero
            marketing support because traditional agency models are too
            expensive.
          </p>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 6 ? 1 : 0
    }} className="mt-12 flex justify-center" data-id="element-762" data-mp="1btaqco9iqk2b"></motion.div>
    </div>;
}